import {
  CustomFieldProps,
  DateFieldProps,
  DynamicField,
  DynamicForm,
  EditorFieldProps,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { InvitationDataTableComponent } from '@/components/shared/invitation-data-table/invitation-data-table.component';
import { PlanningDataTableComponent } from '@/components/shared/planning-data-table/planning-data-table.component';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { User } from '@/types';
import { MEETING_STATUS } from '@/types/meeting.types';
import { BehaviorSubject, map, of } from 'rxjs';

interface IgetEditMeetingFormObjectProps {
  store: MeetingRepository;
  comOptions: SelectOption[];
  createAction?: () => void;
  status?: MEETING_STATUS;
  plannedDossiers: any[];
  comMembers: User[];
  isComDisabled?: BehaviorSubject<boolean>;
}

export const getEditMeetingFormObject = ({
  store,
  comOptions,
  createAction,
  status,
  comMembers,
  isComDisabled,
  plannedDossiers,
}: IgetEditMeetingFormObjectProps): DynamicForm => {
  const comOptionsField: DynamicField<SelectFieldProps> = {
    label: 'meeting.form.com.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservableError('comId'),
    isDisabled: isComDisabled || of(status === MEETING_STATUS.PLANIFIER || status === MEETING_STATUS.NON_PLANIFIER),
    props: {
      value: store.getObservable('comSelected'),
      options: comOptions,
      filterEnabled: true,
    },
  };

  const today = new Date(new Date().setHours(0, 0, 0, 0));

  const dueDateField: DynamicField<DateFieldProps> = {
    label: 'meeting.form.dueDate.label',
    variant: FieldVariant.DATE,
    isRequired: false,
    error: store.getNestedObservableError('reunionDate'),
    props: {
      showTime: true,
      view: 'date',
      minDate: today,
      value: store.getNestedObservable<Date>('dto', 'reunionDate').pipe(map(date => (date ? new Date(date) : null))),
      onChange: (value: Date) => {
        store.setNested('dto', 'reunionDate', value);
        store.clearError('reunionDate');
      },
    },
  };

  const titleField: DynamicField<TextFieldProps> = {
    label: 'meeting.form.title.label',
    variant: FieldVariant.TEXT,
    isRequired: false,
    error: store.getNestedObservableError('emplacement'),
    props: {
      value: store.getNestedObservable<string>('dto', 'emplacement'),
      onChange: (value: string) => {
        store.setNested('dto', 'emplacement', value);
        store.clearError('emplacement');
      },
    },
  };

  const generalObjectField: DynamicField<EditorFieldProps> = {
    label: 'meeting.form.general.label',
    variant: FieldVariant.EDITOR,
    isRequired: true,
    error: store.getNestedObservableError('object'),
    props: {
      height: '200px',
      value: store.getNestedObservable<string>('dto', 'object'),
      onChange: (value: string) => {
        store.setNested('dto', 'object', value);
        store.clearError('object');
      },
    },
  };

  const planningTableField: DynamicField<CustomFieldProps> = {
    label: 'meeting.planning.table.title',
    variant: FieldVariant.CUSTOM,
    isRequired: false,
    error: store.getNestedObservableError('reunionPpms') || store.getNestedObservableError('reunionDacs')
      || store.getNestedObservableError('reunionReports'),
    props: {
      component: PlanningDataTableComponent,
      injectKeys: ['isDisabled', 'data'],
      data: plannedDossiers,
      isDisabled: status === MEETING_STATUS.PLANIFIER || status === MEETING_STATUS.NON_PLANIFIER,
    },
  };

  const invitationTableField: DynamicField<CustomFieldProps> = {
    label: 'meeting.invitation.table.title',
    variant: FieldVariant.CUSTOM,
    isRequired: false,
    error: store.getNestedObservableError('participants'),
    props: {
      component: InvitationDataTableComponent,
      injectKeys: ['createAction', 'memebers', 'isStatusPlanned'],
      createAction: () => createAction?.(),
      members: comMembers,
      isStatusPlanned: status === MEETING_STATUS.PLANIFIER || status === MEETING_STATUS.NON_PLANIFIER,
    },
  };

  return {
    title: 'meeting.form.title',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [comOptionsField, dueDateField],
          },
          {
            fields: [titleField],
          },
          {
            fields: [generalObjectField],
          },
          {
            fields: [planningTableField],
          },
          {
            fields: [invitationTableField],
          },
        ],
      },
    ],
  };
};
